// =================================================================
// Engine: Renal module (PSD §4.2.3, §4.2.4 / TODO Task 1.2)
// Cockcroft-Gault CrCl + population Kel + EI eligibility.
// =================================================================
import { EIEligibility, RenalStability, Sex, } from '@amino-pk/shared-types';
import { err, ok } from './types';
const KEL_SLOPE = 0.0024;
const KEL_INTERCEPT = 0.01;
const KEL_FLOOR = 0.01;
const AGE_FLOOR_NUMERATOR = 0;
/**
 * Cockcroft-Gault creatinine clearance (PSD §4.2.3).
 * CrCl = ((140 − age) × weight) / (72 × SCr) × sexMultiplier.
 */
export function computeCrCl(ageYears, sex, weightKg, scrMgDl) {
    if (!(scrMgDl > 0)) {
        return err('scr-must-be-positive', 'Serum creatinine must be positive.', 'scrMgDl');
    }
    const sexMultiplier = sex === Sex.Male ? 1.0 : 0.85;
    const ageTerm = Math.max(AGE_FLOOR_NUMERATOR, 140 - ageYears);
    return ok((ageTerm * weightKg) / (72 * scrMgDl) * sexMultiplier);
}
/** True when SCr is very low (< 0.3 mg/dL) — CrCl may overestimate. */
export function scrLowFlag(scrMgDl) {
    return scrMgDl > 0 && scrMgDl < 0.3;
}
/**
 * Population Kel from CrCl (PSD §4.2.4). Kel = 0.0024 × CrCl + 0.01, floored at 0.01.
 */
export function estimatePopulationKel(crClMlMin) {
    const kel = KEL_SLOPE * Math.max(0, crClMlMin) + KEL_INTERCEPT;
    return kel < KEL_FLOOR ? KEL_FLOOR : kel;
}
/**
 * Extended-interval eligibility from CrCl and cutoff (PSD §4.2.14b).
 */
export function assessEIEligibility(crClMlMin, renalCutoff, borderlineBand) {
    if (crClMlMin < renalCutoff)
        return EIEligibility.Ineligible;
    if (Math.abs(crClMlMin - renalCutoff) <= borderlineBand)
        return EIEligibility.Borderline;
    return EIEligibility.Eligible;
}
/**
 * Determine renal stability from current and prior SCr (PSD §4.2.14).
 * ΔSCr > 0.3 mg/dL within 24h → Unstable.
 */
export function assessRenalStability(patient) {
    if (patient.previousScrMgDl == null ||
        patient.previousScrMgDl <= 0 ||
        !patient.previousScrTimestamp ||
        !patient.scrTimestamp) {
        return RenalStability.Stable;
    }
    const delta = Math.abs(patient.scrMgDl - patient.previousScrMgDl);
    if (delta <= 0.3)
        return RenalStability.Stable;
    const prevMs = Date.parse(patient.previousScrTimestamp);
    const curMs = Date.parse(patient.scrTimestamp);
    if (Number.isNaN(prevMs) || Number.isNaN(curMs))
        return RenalStability.Stable;
    const diffHr = Math.abs(curMs - prevMs) / 36e5;
    if (diffHr <= 24)
        return RenalStability.Unstable;
    return RenalStability.Stable;
}
/**
 * Inverse Cockcroft-Gault: solve for the SCr that yields a target CrCl (FE-E5).
 * SCr = ((140 − age) × weight × sexMultiplier) / (72 × crClCutoffMlMin).
 * Used to display borderline-CrCl threshold bands in the UI.
 */
export function scrForCrClCutoff(ageYears, sex, weightKg, crClCutoffMlMin) {
    if (!(crClCutoffMlMin > 0)) {
        return err('division-by-zero', 'CrCl cutoff must be positive.', 'crClCutoffMlMin');
    }
    if (!(weightKg > 0)) {
        return err('scr-must-be-positive', 'Weight must be positive.', 'weightKg');
    }
    const sexMultiplier = sex === Sex.Male ? 1.0 : 0.85;
    const ageTerm = Math.max(AGE_FLOOR_NUMERATOR, 140 - ageYears);
    return ok((ageTerm * weightKg * sexMultiplier) / (72 * crClCutoffMlMin));
}
/**
 * Assess SCr trend direction between two measurements (FE-E6).
 * Returns 'rising' / 'falling' / 'flat' / 'unknown' plus the magnitude and
 * the time delta in hours (null when timestamps are missing/unparseable).
 * |delta| < 0.1 mg/dL is treated as flat.
 */
export function assessRenalTrend(currentScrMgDl, previousScrMgDl, previousScrTimestamp, scrTimestamp) {
    if (previousScrMgDl == null ||
        Number.isNaN(Number(previousScrMgDl)) ||
        Number.isNaN(Number(currentScrMgDl))) {
        return { direction: 'unknown', deltaMgDl: 0, deltaHours: null };
    }
    const delta = currentScrMgDl - previousScrMgDl;
    let deltaHours = null;
    if (scrTimestamp && previousScrTimestamp) {
        const curMs = Date.parse(scrTimestamp);
        const prevMs = Date.parse(previousScrTimestamp);
        if (!Number.isNaN(curMs) && !Number.isNaN(prevMs)) {
            deltaHours = Math.abs(curMs - prevMs) / 36e5;
        }
    }
    let direction;
    if (Math.abs(delta) < 0.1)
        direction = 'flat';
    else if (delta > 0)
        direction = 'rising';
    else
        direction = 'falling';
    return { direction, deltaMgDl: delta, deltaHours };
}
/**
 * Assess SCr result staleness (FE-E7).
 * Returns { stale, ageHours } where stale = ageHours > thresholdHrs (default 48h).
 * Missing or unparseable timestamps → { stale: false, ageHours: null } (no warning).
 */
export function computeScrStaleness(scrTimestampISO, referenceISO, thresholdHrs = 48) {
    if (!scrTimestampISO || !referenceISO) {
        return { stale: false, ageHours: null };
    }
    const scrMs = Date.parse(scrTimestampISO);
    const refMs = Date.parse(referenceISO);
    if (Number.isNaN(scrMs) || Number.isNaN(refMs)) {
        return { stale: false, ageHours: null };
    }
    const ageHours = Math.abs(refMs - scrMs) / 36e5;
    return { stale: ageHours > thresholdHrs, ageHours };
}
/**
 * Orchestrator: full RenalResult from patient + dosing weight + protocol.
 */
export function computeRenal(patient, dosingWeightKg, protocol, borderlineBand) {
    const crClRes = computeCrCl(patient.ageYears, patient.sex, dosingWeightKg, patient.scrMgDl);
    if (!crClRes.ok)
        return crClRes;
    const crClMlMin = crClRes.value;
    const kel = estimatePopulationKel(crClMlMin);
    const eiEligibility = assessEIEligibility(crClMlMin, protocol.renalCutoffMlMin, borderlineBand);
    const stability = assessRenalStability(patient);
    return ok({
        crClMlMin,
        crClWeightUsedKg: dosingWeightKg,
        crClScrUsedMgDl: patient.scrMgDl,
        eiEligibility,
        borderlineBandMlMin: borderlineBand,
        stability,
        populationKelPerHr: kel,
    });
}
//# sourceMappingURL=renal.js.map